import {
  ConflictError as SdkConflictError,
  Forbidden,
  NotExist,
  OfflineError as SdkOfflineError,
  UpdateRequiredError as SdkUpdateRequiredError,
  ValidationError as SdkValidationError,
} from '@celeriant/sdk';
import {
  ConflictError,
  DomainError,
  ForbiddenError,
  NotFoundError,
  OfflineError,
  ServerError,
  UpdateRequiredError,
  ValidationError,
} from '@/domain/errors';

// The SDK translation boundary, in one function. The SDK is foreign code: it throws its own
// classes, and a bug inside it throws a TypeError. Above the two gateways that call this,
// `src/services/SessionGateway.ts` and `src/services/ProjectGateway.ts`, only the eight classes in
// domain/errors.ts exist (INVARIANTS rule 17).
//
// It lives in its own module because P2 gave it a second caller. Two copies of this switch would be
// two places for a new SDK error class to be forgotten, and the one that forgot it would flatten a
// distinct user response into `ServerError`.

export function translate(cause: unknown, what: string): DomainError {
  if (cause instanceof DomainError) return cause;
  // Retries exhausted against sustained contention, or a lost ack under replication lag. The
  // caller re-tries the user action with the SAME idempotency key, so a write that actually
  // landed resolves to a dedup hit rather than counting twice.
  if (cause instanceof SdkConflictError) {
    return new ConflictError(`${what}: the aggregate moved under us`, { cause });
  }
  // The app reported no connectivity, so the write was never attempted. Not a server failure and
  // not something a retry ladder can fix.
  if (cause instanceof SdkOfflineError) {
    return new OfflineError(`${what}: the device is offline`, { cause });
  }
  // A precondition failed against fresh server state: the command no longer applies. Re-sending
  // it would not help, which is why it is not a ConflictError.
  if (cause instanceof SdkValidationError) {
    return new ValidationError(cause.kind, `${what}: rejected against the current state`, {
      cause,
    });
  }
  // This build cannot fold what the server is sending, so it has gone read-only rather than emit
  // writes computed against a stale picture. The user needs a reload, not a retry.
  if (cause instanceof SdkUpdateRequiredError) {
    return new UpdateRequiredError(
      `${what}: this device needs updating before it can write again`,
      {
        cause,
      },
    );
  }
  if (cause instanceof NotExist) {
    return new NotFoundError(`aggregate for ${what}`, { cause });
  }
  // No grant on this aggregate: the share code was never redeemed on this device, it was revoked,
  // or a single-use code had already been burned. Also what a non-owner gets for asking to mint.
  // ForbiddenError, not ServerError: nothing is broken and a retry will fail forever, so the
  // screens above say "wrong code" rather than "check the wifi".
  if (cause instanceof Forbidden) {
    return new ForbiddenError(`${what}: this device is not authorised`, { cause });
  }
  // A 409 off the wire, duck-typed rather than caught by class. This is the share-redemption path:
  // `Celeriant.redeemShare` does not go through `OnlineEngine.execute`, so nothing above it has
  // already turned the gateway's optimistic-concurrency refusal into `SdkConflictError` — the raw
  // wire error arrives here and would otherwise land in `ServerError` and tell a coach to check the
  // wifi. The gateway appends grants OCC-conditionally and documents 409 as retryable; 403 is
  // reserved for a genuine denial, so this branch cannot swallow one.
  //
  // Read off the object because the class is unreachable: `@celeriant/wire`'s `Conflict` is banned
  // here (INVARIANTS rule 22) and `@celeriant/sdk` does not re-export it, so there is nothing to
  // `instanceof` against. Written down in docs/upstream-todo.md.
  //
  // Blast radius is the redeem path only. Every guarded write already reaches this function as an
  // `SdkConflictError` and matched the branch at the top.
  if (statusOf(cause) === 409) {
    return new ConflictError(`${what}: the aggregate moved under us`, { cause });
  }
  return new ServerError(cause instanceof Error ? cause.message : String(cause), { cause });
}

// The HTTP status a wire error carries, or undefined for anything that carries none.
function statusOf(cause: unknown): number | undefined {
  if (typeof cause !== 'object' || cause === null) return undefined;
  const status = (cause as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}
